import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { auth } from "../../firebase";
import Sidebar from "../shared/Sidebar";
import "../../css/ContentDuplicateAnalysis.css";

function ContentDuplicateAnalysis() {
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [triggering, setTriggering] = useState(false);
    const [selectedJob, setSelectedJob] = useState(null);
    const [jobDetails, setJobDetails] = useState(null);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [error, setError] = useState(null);
    const [uid, setUid] = useState(null);
    const [pollingJobId, setPollingJobId] = useState(null);
    const navigate = useNavigate();

    // Auth listener
    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (user) {
                setUid(user.uid);
            } else {
                navigate("/login");
            }
        });

        return () => unsubscribe();
    }, [navigate]);

    // Fetch jobs list
    useEffect(() => {
        if (uid) {
            fetchJobs();
        }
    }, [uid]);

    // Poll for running jobs
    useEffect(() => {
        if (pollingJobId) {
            const interval = setInterval(() => {
                fetchJobDetails(pollingJobId);
            }, 3000); // Poll every 3 seconds

            return () => clearInterval(interval);
        }
    }, [pollingJobId]);

    const fetchJobs = async () => {
        try {
            setLoading(true);
            const user = auth.currentUser;
            const token = user ? await user.getIdToken() : null;

            const response = await fetch(
                "https://bluebirddocumentationadmin.pythonanywhere.com/documentation/analyze-content-duplicates/jobs/",
                {
                    headers: {
                        ...(token && { Authorization: `${token}` }),
                    },
                }
            );

            if (response.ok) {
                const data = await response.json();
                setJobs(data.jobs);
                
                // Check if there's a running job to poll
                const runningJob = data.jobs.find(
                    (job) => job.status === "pending" || job.status === "running"
                );
                if (runningJob) {
                    setPollingJobId(runningJob.job_id);
                }
            } else {
                setError("Failed to fetch analysis jobs");
            }
        } catch (err) {
            console.error("Error fetching jobs:", err);
            setError("Error loading jobs");
        } finally {
            setLoading(false);
        }
    };

    const fetchJobDetails = async (jobId) => {
        try {
            setLoadingDetails(true);
            const user = auth.currentUser;
            const token = user ? await user.getIdToken() : null;

            const response = await fetch(
                `https://bluebirddocumentationadmin.pythonanywhere.com/documentation/analyze-content-duplicates/jobs/${jobId}/`,
                {
                    headers: {
                        ...(token && { Authorization: `${token}` }),
                    },
                }
            );

            if (response.ok) {
                const data = await response.json();
                setJobDetails(data);

                // Stop polling if job is no longer running
                if (data.status !== "pending" && data.status !== "running") {
                    setPollingJobId(null);
                    // Refresh jobs list to update status
                    fetchJobs();
                }
            } else {
                setError("Failed to fetch job details");
            }
        } catch (err) {
            console.error("Error fetching job details:", err);
            setError("Error loading job details");
        } finally {
            setLoadingDetails(false);
        }
    };

    const triggerAnalysis = async () => {
        try {
            setTriggering(true);
            setError(null);
            const user = auth.currentUser;
            const token = user ? await user.getIdToken() : null;

            const response = await fetch(
                "https://bluebirddocumentationadmin.pythonanywhere.com/documentation/analyze-content-duplicates/",
                {
                    method: "POST",
                    headers: {
                        ...(token && { Authorization: `${token}` }),
                    },
                }
            );

            if (response.ok) {
                const data = await response.json();
                setPollingJobId(data.job_id);
                fetchJobs(); // Refresh the jobs list
            } else if (response.status === 409) {
                const data = await response.json();
                setError(data.error);
            } else {
                setError("Failed to start analysis");
            }
        } catch (err) {
            console.error("Error triggering analysis:", err);
            setError("Error starting analysis");
        } finally {
            setTriggering(false);
        }
    };

    const deleteJob = async (jobId) => {
        if (!window.confirm("Are you sure you want to delete this analysis job?")) {
            return;
        }

        try {
            const user = auth.currentUser;
            const token = user ? await user.getIdToken() : null;

            const response = await fetch(
                `https://bluebirddocumentationadmin.pythonanywhere.com/documentation/analyze-content-duplicates/jobs/${jobId}/delete/`,
                {
                    method: "DELETE",
                    headers: {
                        ...(token && { Authorization: `${token}` }),
                    },
                }
            );

            if (response.ok) {
                // Refresh jobs list
                fetchJobs();
                // Clear selected job if it was deleted
                if (selectedJob === jobId) {
                    setSelectedJob(null);
                    setJobDetails(null);
                }
            } else {
                const data = await response.json();
                setError(data.error || "Failed to delete job");
            }
        } catch (err) {
            console.error("Error deleting job:", err);
            setError("Error deleting job");
        }
    };

    const handleJobClick = (jobId) => {
        setSelectedJob(jobId);
        fetchJobDetails(jobId);
    };

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        const date = new Date(dateString);
        return date.toLocaleString();
    };

    const getStatusBadge = (status) => {
        const statusClasses = {
            pending: "ContentDuplicateAnalysis__status-pending",
            running: "ContentDuplicateAnalysis__status-running",
            completed: "ContentDuplicateAnalysis__status-completed",
            failed: "ContentDuplicateAnalysis__status-failed",
        };

        return (
            <span className={`ContentDuplicateAnalysis__status-badge ${statusClasses[status] || ""}`}>
                {status.toUpperCase()}
            </span>
        );
    };

    const renderResults = () => {
        if (!jobDetails || !jobDetails.results) return null;

        const { results } = jobDetails;

        return (
            <div className="ContentDuplicateAnalysis__results-container">
                <h3>Analysis Results</h3>
                <div className="ContentDuplicateAnalysis__results-summary">
                    <div className="ContentDuplicateAnalysis__summary-item">
                        <strong>Documents Analyzed:</strong> {results.total_documents_analyzed || 0}
                    </div>
                    <div className="ContentDuplicateAnalysis__summary-item">
                        <strong>Duplicates Found:</strong> {results.total_duplicates_found || 0}
                    </div>
                </div>

                {results.duplicates && results.duplicates.length > 0 ? (
                    <div className="ContentDuplicateAnalysis__duplicates-list">
                        <h4>Duplicate Content Details</h4>
                        {results.duplicates.map((dup, index) => (
                            <div key={index} className="ContentDuplicateAnalysis__duplicate-item">
                                <div className="ContentDuplicateAnalysis__duplicate-header">
                                    <strong>Duplicate Set #{index + 1}</strong>
                                    <span className="ContentDuplicateAnalysis__similarity-score">
                                        Similarity: {(dup.similarity_score * 100).toFixed(1)}%
                                    </span>
                                </div>
                                <div className="ContentDuplicateAnalysis__duplicate-documents">
                                    <div className="ContentDuplicateAnalysis__document-info">
                                        <strong>Document 1:</strong>
                                        <a
                                            href={`/documentation/${dup.document_1.document_type}/${dup.document_1.name}/${uid}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="ContentDuplicateAnalysis__document-link"
                                        >
                                            {dup.document_1.name} ({dup.document_1.document_type})
                                        </a>
                                    </div>
                                    <div className="ContentDuplicateAnalysis__document-info">
                                        <strong>Document 2:</strong>
                                        <a
                                            href={`/documentation/${dup.document_2.document_type}/${dup.document_2.name}/${uid}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="ContentDuplicateAnalysis__document-link"
                                        >
                                            {dup.document_2.name} ({dup.document_2.document_type})
                                        </a>
                                    </div>
                                </div>
                                {dup.content_preview_1 && (
                                    <div className="ContentDuplicateAnalysis__duplicate-content">
                                        <strong>Duplicate Content Preview:</strong>
                                        <pre>{dup.content_preview_1.substring(0, 200)}...</pre>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="ContentDuplicateAnalysis__no-duplicates">
                        <p>No duplicate content found in your documents. Great job keeping your documentation clean!</p>
                    </div>
                )}
            </div>
        );
    };

    const hasRunningJob = jobs.some(
        (job) => job.status === "pending" || job.status === "running"
    );

    return (
        <div className="ContentDuplicateAnalysis__page">
            <Sidebar />
            <div className="ContentDuplicateAnalysis__container">
                <div className="ContentDuplicateAnalysis__page-header">
                    <h1>Content Duplicate Analysis</h1>
                    <p className="ContentDuplicateAnalysis__page-description">
                        Analyze your documentation to find duplicate or similar content across your documents.
                    </p>
                </div>

                {error && <div className="ContentDuplicateAnalysis__error-message">{error}</div>}

                <div className="ContentDuplicateAnalysis__action-section">
                    <button
                        onClick={triggerAnalysis}
                        disabled={triggering || hasRunningJob}
                        className="ContentDuplicateAnalysis__trigger-button"
                    >
                        {triggering ? (
                            <>
                                <i className="fas fa-spinner fa-spin"></i> Starting Analysis...
                            </>
                        ) : hasRunningJob ? (
                            "Analysis In Progress..."
                        ) : (
                            <>
                                <i className="fas fa-search"></i> Start New Analysis
                            </>
                        )}
                    </button>
                    {hasRunningJob && (
                        <p className="ContentDuplicateAnalysis__info-text">
                            An analysis is currently running. Results will appear when complete.
                        </p>
                    )}
                </div>

                <div className="ContentDuplicateAnalysis__content-layout">
                    <div className="ContentDuplicateAnalysis__jobs-section">
                        <h2>Analysis History</h2>
                        {loading ? (
                            <div className="ContentDuplicateAnalysis__loading-spinner">
                                <i className="fas fa-spinner fa-spin"></i> Loading...
                            </div>
                        ) : jobs.length === 0 ? (
                            <p className="ContentDuplicateAnalysis__no-jobs">No analysis jobs yet. Start your first analysis above!</p>
                        ) : (
                            <div className="ContentDuplicateAnalysis__jobs-list">
                                {jobs.map((job) => (
                                    <div
                                        key={job.job_id}
                                        className={`ContentDuplicateAnalysis__job-card ${selectedJob === job.job_id ? "ContentDuplicateAnalysis__selected" : ""}`}
                                        onClick={() => handleJobClick(job.job_id)}
                                    >
                                        <div className="ContentDuplicateAnalysis__job-header">
                                            <span className="ContentDuplicateAnalysis__job-id">Job #{job.job_id}</span>
                                            {getStatusBadge(job.status)}
                                        </div>
                                        <div className="ContentDuplicateAnalysis__job-meta">
                                            <div className="ContentDuplicateAnalysis__job-date">
                                                <i className="far fa-clock"></i> {formatDate(job.created_at)}
                                            </div>
                                            {job.summary && (
                                                <div className="ContentDuplicateAnalysis__job-summary">
                                                    <span>{job.summary.total_documents_analyzed} docs analyzed</span>
                                                    <span className="ContentDuplicateAnalysis__separator">•</span>
                                                    <span>{job.summary.total_duplicates_found} duplicates</span>
                                                </div>
                                            )}
                                        </div>
                                        {job.status !== "pending" && job.status !== "running" && (
                                            <button
                                                className="ContentDuplicateAnalysis__delete-job-button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    deleteJob(job.job_id);
                                                }}
                                                title="Delete job"
                                            >
                                                <i className="fas fa-trash"></i>
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="ContentDuplicateAnalysis__details-section">
                        {selectedJob ? (
                            <>
                                {loadingDetails ? (
                                    <div className="ContentDuplicateAnalysis__loading-spinner">
                                        <i className="fas fa-spinner fa-spin"></i> Loading details...
                                    </div>
                                ) : jobDetails ? (
                                    <div className="ContentDuplicateAnalysis__job-details">
                                        <div className="ContentDuplicateAnalysis__details-header">
                                            <h2>Job #{jobDetails.job_id} Details</h2>
                                            {getStatusBadge(jobDetails.status)}
                                        </div>
                                        <div className="ContentDuplicateAnalysis__details-meta">
                                            <div className="ContentDuplicateAnalysis__meta-item">
                                                <strong>Created:</strong> {formatDate(jobDetails.created_at)}
                                            </div>
                                            {jobDetails.started_at && (
                                                <div className="ContentDuplicateAnalysis__meta-item">
                                                    <strong>Started:</strong> {formatDate(jobDetails.started_at)}
                                                </div>
                                            )}
                                            {jobDetails.completed_at && (
                                                <div className="ContentDuplicateAnalysis__meta-item">
                                                    <strong>Completed:</strong> {formatDate(jobDetails.completed_at)}
                                                </div>
                                            )}
                                        </div>

                                        {jobDetails.status === "failed" && jobDetails.error_message && (
                                            <div className="ContentDuplicateAnalysis__error-details">
                                                <strong>Error:</strong>
                                                <p>{jobDetails.error_message}</p>
                                            </div>
                                        )}

                                        {jobDetails.status === "completed" && renderResults()}

                                        {(jobDetails.status === "pending" || jobDetails.status === "running") && (
                                            <div className="ContentDuplicateAnalysis__processing-info">
                                                <i className="fas fa-spinner fa-spin"></i>
                                                <p>Analysis is {jobDetails.status}. This may take a few minutes...</p>
                                            </div>
                                        )}
                                    </div>
                                ) : null}
                            </>
                        ) : (
                            <div className="ContentDuplicateAnalysis__no-selection">
                                <i className="fas fa-hand-pointer"></i>
                                <p>Select a job from the list to view details</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ContentDuplicateAnalysis;
